import { c as _c } from "react/compiler-runtime";
import { ProjectsOverviewEmptyPage } from "@components/common/empty-page/projects-overview-empty-page";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { ProjectsSort } from "@components/projects/projects-sort";
import { useProjectsFilters } from "@components/projects/projects-filters/use-projects-filters";
import { BulkChangeProjectStatus } from "@components/table/projects/bulk-actions/bulk-change-project-status";
import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { ProjectsTable } from "@components/table/projects/projects-table";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { useFetchProjects } from "@hooks/projects/use-fetch-projects";
import { useFilteredStreamedProjects } from "@hooks/projects/use-filtered-streamed-projects";
import { useMediaQueryList } from "@hooks/use-media-query";
import { ProjectsMap } from "@pages/projects/projects-map/projects-map";
import { ProjectsMobileView } from "@pages/projects/projects-mobile-view";
import { ProjectsTiles } from "@pages/projects/projects-tiles";
import { useProjectTableColumnsConfig } from "@pages/projects/projects-utils";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { useMemo } from "react";
import { CreateProjectDialog } from "@pages/projects/create-project-dialog";
/**
 * The projects tab listing all the active or archived projects (depending on the props) in the company
 */
export function ProjectsOverview(t0) {
  const $ = _c(86);
  const {
    companyId,
    projectArchivingState,
    shouldForceLoading
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectedEntitiesSelector("projects");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const selectedEntities = useAppSelector(t1);
  const {
    projects: viewMode
  } = useAppSelector(dataViewModeSelector);
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const {
    columns: projectTableColumns,
    defaultVisibleColumns: projectTableDefaultVisibleColumns,
    isCompanyContextReady: isProjectTableConfigReady
  } = useProjectTableColumnsConfig();
  const isMapViewMode = viewMode === "map" && !isScreenSmOrSmaller;
  let t2;
  if ($[1] !== projectArchivingState) {
    t2 = {
      projectArchivingState
    };
    $[1] = projectArchivingState;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    isAnyFilterApplied,
    filterProjects,
    filterChips
  } = useProjectsFilters(t2);
  const t3 = !isMapViewMode && !isAnyFilterApplied;
  let t4;
  if ($[3] !== companyId || $[4] !== projectArchivingState || $[5] !== t3) {
    t4 = {
      companyId,
      projectArchivingState,
      isEnabled: t3
    };
    $[3] = companyId;
    $[4] = projectArchivingState;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const paginatedProjects = useFetchProjects(t4);
  const {
    sortBy,
    setSortBy,
    search,
    isDebouncingSearch
  } = paginatedProjects;
  const t5 = !isMapViewMode && isAnyFilterApplied;
  let t6;
  if ($[7] !== companyId || $[8] !== filterProjects || $[9] !== projectArchivingState || $[10] !== search || $[11] !== sortBy || $[12] !== t5) {
    t6 = {
      companyId,
      projectArchivingState,
      sortBy,
      search,
      filterProjects,
      isEnabled: t5
    };
    $[7] = companyId;
    $[8] = filterProjects;
    $[9] = projectArchivingState;
    $[10] = search;
    $[11] = sortBy;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const filteredProjects = useFilteredStreamedProjects(t6);
  const {
    projects,
    isFetchingProjects,
    hasFetchedFirstPage,
    nextPageCursor,
    loadMoreProjects,
    totalCount
  } = isAnyFilterApplied ? filteredProjects : paginatedProjects;
  const isLoading = isFetchingProjects && hasFetchedFirstPage || isDebouncingSearch || shouldForceLoading || !hasFetchedFirstPage;
  const shouldShowEmptyPage = !shouldForceLoading && !isFetchingProjects && hasFetchedFirstPage && projects.length === 0;
  const isAnyRowSelected = selectedEntities.length !== 0;
  const shouldRenderActions = projects.length > 0;
  if (isMapViewMode) {
    let t7;
    if ($[14] !== companyId || $[15] !== projectArchivingState || $[16] !== setSortBy || $[17] !== sortBy) {
      t7 = <ProjectsMap companyId={companyId} projectArchivingState={projectArchivingState} sortBy={sortBy} setSortBy={setSortBy} />;
      $[14] = companyId;
      $[15] = projectArchivingState;
      $[16] = setSortBy;
      $[17] = sortBy;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    return t7;
  }
  let t7;
  if ($[19] !== shouldShowEmptyPage) {
    t7 = !shouldShowEmptyPage && <CreateProjectDialog />;
    $[19] = shouldShowEmptyPage;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== setSortBy || $[22] !== shouldRenderActions || $[23] !== sortBy) {
    t8 = shouldRenderActions && <ProjectsSort key="projects-sort" sortBy={sortBy} setSortBy={setSortBy} />;
    $[21] = setSortBy;
    $[22] = shouldRenderActions;
    $[23] = sortBy;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== companyId || $[26] !== isAnyRowSelected || $[27] !== projectArchivingState || $[28] !== projects || $[29] !== selectedEntities || $[30] !== shouldRenderActions || $[31] !== viewMode) {
    t9 = shouldRenderActions && <BulkExportProjects companyId={companyId} selectedProjects={isAnyRowSelected ? selectedEntities : projects} subjectType="workspace" projectStatus={projectArchivingState} areEntitiesSelected={isAnyRowSelected} buttonType="standard" viewMode={viewMode} />;
    $[25] = companyId;
    $[26] = isAnyRowSelected;
    $[27] = projectArchivingState;
    $[28] = projects;
    $[29] = selectedEntities;
    $[30] = shouldRenderActions;
    $[31] = viewMode;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== filterChips || $[34] !== isAnyFilterApplied || $[35] !== isLoading || $[36] !== t7 || $[37] !== t8 || $[38] !== t9 || $[39] !== totalCount) {
    t10 = <EntitiesToolbar toolbarEntityName="Project" numberOfRows={totalCount} isLoading={isLoading} filterChips={filterChips} isAnyFilterApplied={isAnyFilterApplied} mobileAction={t7} sortActions={t8} exportComponent={t9} />;
    $[33] = filterChips;
    $[34] = isAnyFilterApplied;
    $[35] = isLoading;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = totalCount;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== isAnyFilterApplied || $[42] !== projectArchivingState || $[43] !== projects || $[44] !== search || $[45] !== shouldShowEmptyPage) {
    t11 = shouldShowEmptyPage && <><ProjectsOverviewEmptyPage projectArchivingState={projectArchivingState} isSearching={search !== undefined} isFiltering={isAnyFilterApplied} /><BulkChangeProjectStatus projectStatus={projectArchivingState} projects={projects} /></>;
    $[41] = isAnyFilterApplied;
    $[42] = projectArchivingState;
    $[43] = projects;
    $[44] = search;
    $[45] = shouldShowEmptyPage;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== isLoading || $[48] !== isScreenSmOrSmaller || $[49] !== loadMoreProjects || $[50] !== projectArchivingState || $[51] !== projects || $[52] !== shouldShowEmptyPage || $[53] !== sortBy) {
    t12 = isScreenSmOrSmaller && !shouldShowEmptyPage && <ProjectsMobileView projects={projects} sortBy={sortBy} isLoading={isLoading} projectArchivingState={projectArchivingState} loadMoreProjects={loadMoreProjects} />;
    $[47] = isLoading;
    $[48] = isScreenSmOrSmaller;
    $[49] = loadMoreProjects;
    $[50] = projectArchivingState;
    $[51] = projects;
    $[52] = shouldShowEmptyPage;
    $[53] = sortBy;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  let t13;
  if ($[55] !== companyId || $[56] !== isLoading || $[57] !== isProjectTableConfigReady || $[58] !== isScreenSmOrSmaller || $[59] !== loadMoreProjects || $[60] !== nextPageCursor || $[61] !== projectArchivingState || $[62] !== projectTableColumns || $[63] !== projectTableDefaultVisibleColumns || $[64] !== projects || $[65] !== setSortBy || $[66] !== shouldShowEmptyPage || $[67] !== sortBy || $[68] !== totalCount || $[69] !== viewMode) {
    t13 = viewMode === "list" && !isScreenSmOrSmaller && !shouldShowEmptyPage && isProjectTableConfigReady && <ColumnPreferencesProvider tableKey="projects-table" columns={projectTableColumns} options={{
      nonConfigurableColumns: ["options"],
      defaultVisibleColumns: projectTableDefaultVisibleColumns
    }}><ProjectsTable projects={projects} companyId={companyId} isLoading={isLoading} nextPageCursor={nextPageCursor} loadMoreProjects={loadMoreProjects} sortBy={sortBy} setSortBy={setSortBy} projectArchivingState={projectArchivingState} totalCount={totalCount} /></ColumnPreferencesProvider>;
    $[55] = companyId;
    $[56] = isLoading;
    $[57] = isProjectTableConfigReady;
    $[58] = isScreenSmOrSmaller;
    $[59] = loadMoreProjects;
    $[60] = nextPageCursor;
    $[61] = projectArchivingState;
    $[62] = projectTableColumns;
    $[63] = projectTableDefaultVisibleColumns;
    $[64] = projects;
    $[65] = setSortBy;
    $[66] = shouldShowEmptyPage;
    $[67] = sortBy;
    $[68] = totalCount;
    $[69] = viewMode;
    $[70] = t13;
  } else {
    t13 = $[70];
  }
  let t14;
  if ($[71] !== companyId || $[72] !== isLoading || $[73] !== isScreenSmOrSmaller || $[74] !== loadMoreProjects || $[75] !== projects || $[76] !== shouldShowEmptyPage || $[77] !== sortBy || $[78] !== viewMode) {
    t14 = viewMode === "cards" && !isScreenSmOrSmaller && !shouldShowEmptyPage && <ProjectsTiles projects={projects} companyId={companyId} isLoading={isLoading} loadMoreProjects={loadMoreProjects} sortBy={sortBy} />;
    $[71] = companyId;
    $[72] = isLoading;
    $[73] = isScreenSmOrSmaller;
    $[74] = loadMoreProjects;
    $[75] = projects;
    $[76] = shouldShowEmptyPage;
    $[77] = sortBy;
    $[78] = viewMode;
    $[79] = t14;
  } else {
    t14 = $[79];
  }
  let t15;
  if ($[80] !== t10 || $[81] !== t11 || $[82] !== t12 || $[83] !== t13 || $[84] !== t14) {
    t15 = <>{t10}{t11}{t12}{t13}{t14}</>;
    $[80] = t10;
    $[81] = t11;
    $[82] = t12;
    $[83] = t13;
    $[84] = t14;
    $[85] = t15;
  } else {
    t15 = $[85];
  }
  return t15;
}